import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  useCohorts,
  useGlobalSettings,
  useUpdateGlobalSettings,
} from 'hooks/api/use-global-settings';

import toast from 'components/partials/toast/toast';

import { monitoringDashboardMetricsKeys } from 'containers/user/critical-elements-monitoring/hooks/use-monitoring-dashboard-metrics';

import { CohortStageSettingDtoStage as CohortStage } from 'api/generated/schemas';
import type {
  CohortStageSettingDto,
  CohortStageSettingDtoStage,
} from 'api/generated/schemas';

export interface GlobalSettingsRow {
  cohortName: string;
  admit: boolean;
  netDeposit: boolean;
}

type PairMap = Map<string, CohortStageSettingDto>;

export interface UseGlobalSettingsPageResult {
  rows: GlobalSettingsRow[];
  isLoading: boolean;
  isError: boolean;
  isEmpty: boolean;
  isDirty: boolean;
  isSaving: boolean;
  unsavedChangesCount: number;
  handleToggle: (cohortName: string, stage: CohortStageSettingDtoStage) => void;
  handleToggleAll: (stage: CohortStageSettingDtoStage) => void;
  handleSave: () => void;
  handleCancel: () => void;
}

const getCellKey = (
  stage: CohortStageSettingDtoStage,
  cohortName: string
): string => `${stage}:${cohortName}`;

const countDifferingKeys = (a: PairMap, b: PairMap): number =>
  [...a.keys()].filter((key) => !b.has(key)).length +
  [...b.keys()].filter((key) => !a.has(key)).length;

export const useGlobalSettingsPage = (): UseGlobalSettingsPageResult => {
  const queryClient = useQueryClient();
  const cohortsQuery = useCohorts();
  const settingsQuery = useGlobalSettings();
  const { mutate, isPending: isSaving } = useUpdateGlobalSettings();
  const [draftPairs, setDraftPairs] = useState<PairMap | null>(null);

  const savedPairs = useMemo<PairMap>(
    () =>
      new Map(
        (settingsQuery.data?.enabledCohortStages ?? []).map((pair) => [
          getCellKey(pair.stage, pair.cohortName),
          pair,
        ])
      ),
    [settingsQuery.data]
  );
  const currentPairs = draftPairs ?? savedPairs;

  const rows = useMemo<GlobalSettingsRow[]>(
    () =>
      (cohortsQuery.data ?? []).flatMap(({ name }) =>
        name
          ? [
              {
                cohortName: name,
                admit: currentPairs.has(getCellKey(CohortStage.ADMIT, name)),
                netDeposit: currentPairs.has(
                  getCellKey(CohortStage.NET_DEPOSIT, name)
                ),
              },
            ]
          : []
      ),
    [cohortsQuery.data, currentPairs]
  );

  const unsavedChangesCount =
    draftPairs === null ? 0 : countDifferingKeys(draftPairs, savedPairs);
  const isDirty = unsavedChangesCount > 0;
  const isLoading = cohortsQuery.isLoading || settingsQuery.isLoading;
  const isError = cohortsQuery.isError || settingsQuery.isError;
  const isEmpty = !isLoading && !isError && !rows.length;

  const handleToggle = useCallback(
    (cohortName: string, stage: CohortStageSettingDtoStage) => {
      const key = getCellKey(stage, cohortName);

      setDraftPairs((previous) => {
        const next = new Map(previous ?? savedPairs);

        if (next.has(key)) {
          next.delete(key);
        } else {
          next.set(key, { cohortName, stage });
        }

        return next;
      });
    },
    [savedPairs]
  );

  const handleToggleAll = useCallback(
    (stage: CohortStageSettingDtoStage) => {
      const stageKeys = rows.map(({ cohortName }) => ({
        cohortName,
        key: getCellKey(stage, cohortName),
      }));

      setDraftPairs((previous) => {
        const next = new Map(previous ?? savedPairs);
        const allChecked = stageKeys.every(({ key }) => next.has(key));

        stageKeys.forEach(({ cohortName, key }) => {
          if (allChecked) {
            next.delete(key);
          } else if (!next.has(key)) {
            next.set(key, { cohortName, stage });
          }
        });

        return next;
      });
    },
    [rows, savedPairs]
  );

  const handleSave = useCallback(() => {
    mutate(
      { enabledCohortStages: [...currentPairs.values()] },
      {
        onSuccess: () => {
          setDraftPairs(null);
          queryClient.invalidateQueries({
            queryKey: monitoringDashboardMetricsKeys.all,
          });
          toast.success({ title: 'Global settings saved' });
        },
        onError: () => {
          toast.error({ title: 'Failed to save global settings' });
        },
      }
    );
  }, [mutate, currentPairs, queryClient]);

  const handleCancel = useCallback(() => setDraftPairs(null), []);

  return {
    rows,
    isLoading,
    isError,
    isEmpty,
    isDirty,
    isSaving,
    unsavedChangesCount,
    handleToggle,
    handleToggleAll,
    handleSave,
    handleCancel,
  };
};
